import { useRouter } from "next/router"
import invariant from "tiny-invariant"

import { useAddress } from "web3/hooks/useAddress"
import type {
  CreateTallyProposalMutation,
  CreateTallyProposalMutationVariables,
  GovernorContract,
  Governor,
  GovernorParameters,
  Token,
  ExecutableCallInput,
} from "query/graphql"
import { CreateTallyProposalDocument, ExecutableCallType } from "query/graphql"
import type { CreateProposalFormValues } from "createProposal/types/form"
import { useGovernor } from "governance/hooks/useGovernor"
import { useMutation } from "query/hooks/useMutation"
import { ROUTES } from "common/constants/routes"
import { GrpcErrorType } from "common/helpers/fetcher"
import { useToast } from "common/hooks/useToast"
import { getWhiteLabelRoute } from "whitelabel/utils/breadcrumb"
import EmptyAction from "createProposal/action/EmptyAction"
import { useCreateProposal } from "createProposal/providers/CreateProposalProvider"

type CreateTallyProposalValues = {
  actions: CreateProposalFormValues["actions"]
  details: CreateProposalFormValues["details"]
  txHash?: string
  snapshotURL?: string
  originalProposalId?: string
}

type UseCreateTallyProposal = {
  isLoading: boolean
  doesImplementBravo: boolean | undefined
  createTallyProposal: (values: CreateTallyProposalValues) => void
  isError: boolean
}

export const useCreateTallyProposal = (
  governance: Pick<Governor, "chainId" | "id"> & {
    contracts: {
      governor: Pick<GovernorContract, "address" | "type">
    }
    parameters: Pick<GovernorParameters, "proposalThreshold">
    token: Token
  },
  isWhiteLabel: boolean,
  slug: string,
  originalProposalId?: string,
): UseCreateTallyProposal => {
  const { id, contracts } = governance
  const { governor } = contracts

  const router = useRouter()
  const address = useAddress()
  const { toast } = useToast()
  const { doesImplementBravo } = useGovernor(governor.address)
  const { setFormState } = useCreateProposal()

  const {
    mutate: createTallyProposalMutate,
    isPending: isCreateTallyProposalLoading,
    isError: isCreateTallyProposalError,
  } = useMutation<
    CreateTallyProposalMutation,
    CreateTallyProposalMutationVariables
  >(
    {
      query: CreateTallyProposalDocument,
      messages: {
        gql: {
          descriptions: {
            [GrpcErrorType.AlreadyExists]: "The draft already exists",
          },
          titles: {
            [GrpcErrorType.AlreadyExists]:
              "Try again using a different description",
          },
        },
      },
      ...(originalProposalId
        ? { omittedErrors: [GrpcErrorType.AlreadyExists] }
        : undefined),
      onError: async () => {
        // Redirect to draft page
        if (originalProposalId) {
          setFormState("success")

          // Wait for 2 second before redirecting
          await new Promise((resolve) => setTimeout(resolve, 2000))

          router.push(
            isWhiteLabel
              ? getWhiteLabelRoute(
                  ROUTES.governance.draft.index(slug, originalProposalId),
                )
              : ROUTES.governance.draft.index(slug, originalProposalId),
          )
        }
      },
    },
    {
      onSuccess: async (data) => {
        if (!data) return

        const { createProposal } = data

        const isSubmittedOnchain = Boolean(createProposal?.metadata?.txHash)

        if (!isSubmittedOnchain) {
          toast({
            status: "success",
            title: originalProposalId ? "Draft updated" : "Draft created",
          })
        }

        const redirectDraftId = originalProposalId ?? createProposal.id

        setFormState("success")

        // Wait for 5 seconds before redirecting
        await new Promise((resolve) => setTimeout(resolve, 5000))

        router.push(
          isWhiteLabel
            ? getWhiteLabelRoute(
                ROUTES.governance.draft.index(slug, redirectDraftId),
              )
            : ROUTES.governance.draft.index(slug, redirectDraftId),
        )
      },
    },
  )

  const createTallyProposal = ({
    details,
    actions,
    txHash,
    originalProposalId,
  }: CreateTallyProposalValues) => {
    const { title, description, snapshotURL, discourseURL } = details

    invariant(
      doesImplementBravo !== undefined,
      "`doesImplementBravo` must be defined",
    )

    invariant(actions, "`actions` must be defined")

    // Fixes a bug where a [EmptyAction, TransferAction] causes two executable calls instead of one
    const actionsToEncode =
      actions.length === 1
        ? actions
        : actions.filter((action) => action.recipe !== ExecutableCallType.Empty)

    const encodedActions = actionsToEncode.reduce((result, action) => {
      const encoded = action.encodeForTallyApi()

      if (!encoded) return result

      if (Array.isArray(encoded)) return [...result, ...encoded]

      return [...result, encoded]
    }, [] as ExecutableCallInput[])

    const governorExecutableCalls =
      encodedActions?.length > 0
        ? encodedActions
        : (new EmptyAction({
            targetAddress: address,
          }).encodeForTallyApi() as ExecutableCallInput[])

    createTallyProposalMutate({
      input: {
        governorId: id,
        title,
        description,
        executableCalls: governorExecutableCalls,
        txHash,
        originalId: originalProposalId,
        snapshotURL,
        discourseURL,
      },
    })
  }

  return {
    doesImplementBravo,
    isLoading: isCreateTallyProposalLoading,
    isError: isCreateTallyProposalError,
    createTallyProposal,
  }
}
